// Historial de UNA política especial (`GET /store-policies/{policyId}/log`): fecha · qué pasó ·
// quién · motivo. Reemplaza al `HistoryList` de mock, que se alimentaba de la bitácora de planes.
import { cn } from '@/lib/cn';
import { formatDateMx, formatTimeMx } from '@/lib/dateHelpers';
import {
    POLICY_LOG_ACTION_LABEL,
    POLICY_STATUS_LABEL,
    type PolicyStatus,
    type StorePolicyLogEntry,
} from '../types';

const BADGE_CLASS: Record<StorePolicyLogEntry['action'], string> = {
    CREATE: 'bg-muted text-muted-foreground',
    UPDATE: 'bg-primary/10 text-primary',
    STATUS_CHANGE: 'bg-info/10 text-info',
};

/** Un `status` del wire como lo lee una persona; cualquier otra cosa se muestra tal cual. */
function statusText(value: unknown): string {
    if (typeof value !== 'string') return '—';
    const known = Object.hasOwn(POLICY_STATUS_LABEL, value);
    return known ? POLICY_STATUS_LABEL[value as PolicyStatus] : value;
}

/**
 * Qué dice el renglón. El cambio de estado se lee «de X a Y»; el alta y la edición solo se nombran:
 * el snapshot del efecto es un objeto del wire y pintarlo crudo no le dice nada a nadie.
 */
function describeEntry(entry: StorePolicyLogEntry): string {
    if (entry.action === 'STATUS_CHANGE') {
        return `Estado: ${statusText(entry.oldValue)} → ${statusText(entry.newValue)}`;
    }
    if (entry.action === 'CREATE') return 'Política creada';
    const from = entry.versionFrom;
    const to = entry.versionTo;
    if (from !== null && to !== null) return `Efecto editado · v${String(from)} → v${String(to)}`;
    return 'Efecto editado';
}

export function PolicyHistory({
    entries,
    isLoading,
    emptyMessage,
}: {
    entries: readonly StorePolicyLogEntry[];
    isLoading: boolean;
    emptyMessage: string;
}) {
    if (isLoading) {
        return (
            <div className="rounded-md border border-border bg-card px-3 py-3 text-center text-[10.5px] italic text-muted-foreground">
                Cargando historial…
            </div>
        );
    }

    return (
        <div className="overflow-hidden rounded-md border border-border bg-card">
            {entries.length === 0 ? (
                <div className="px-3 py-3 text-center text-[10.5px] italic text-muted-foreground">
                    {emptyMessage}
                </div>
            ) : (
                entries.map((entry, i) => {
                    // El nombre resuelto si lo hay; si no, el actor VERBATIM del backend, que hoy
                    // suele ser `system` o el cognitoSub crudo.
                    const actor = entry.performedByName ?? entry.performedBy;
                    const detail = [actor, entry.reason].filter(Boolean).join(' · ');
                    return (
                        <div
                            key={`${entry.occurredAt}-${String(i)}`}
                            className="grid grid-cols-[92px_auto_1fr] items-center gap-2 border-b border-border/40 px-2.5 py-[5px] text-[10.5px] last:border-b-0"
                        >
                            <span className="tabular-nums text-muted-foreground">
                                {entry.occurredAt === ''
                                    ? '—'
                                    : `${formatDateMx(entry.occurredAt)} ${formatTimeMx(entry.occurredAt)}`}
                            </span>
                            <span
                                className={cn(
                                    'inline-flex items-center rounded-[3px] px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wide',
                                    BADGE_CLASS[entry.action],
                                )}
                            >
                                {POLICY_LOG_ACTION_LABEL[entry.action]}
                            </span>
                            <span>
                                {describeEntry(entry)}
                                {detail === '' ? null : (
                                    <span className="text-muted-foreground"> · {detail}</span>
                                )}
                            </span>
                        </div>
                    );
                })
            )}
        </div>
    );
}
